import { debounce } from "../src/shared/Debounce";
import {
  GOOGLE_PREDICTION_DEBOUNCE_MS,
  SEARCH_AREA_INPUT_SELECTOR,
  SEARCH_AREA_SUGGESTIONS_SELECTOR,
} from "./bookingForm/searchAreaInput/constants";
import { ensureGooglePlacesLoaded } from "./bookingForm/searchAreaInput/ensureGooglePlacesLoaded";
import { fetchPredictions } from "./bookingForm/searchAreaInput/fetchPredictions";
import { formatLocationLabel } from "./bookingForm/searchAreaInput/formatLocationLabel";
import {
  clearSuggestions,
  getClickedSuggestionIndex,
  renderSuggestions,
  updateHighlightedSuggestion,
} from "./bookingForm/searchAreaInput/suggestionsView";

const initSearchAreaInput = async (areaInput: HTMLInputElement) => {
  const suggestionsContainer =
    areaInput.parentElement?.querySelector<HTMLDivElement>(
      SEARCH_AREA_SUGGESTIONS_SELECTOR
    );

  if (!suggestionsContainer) {
    return;
  }

  if (areaInput.dataset.searchAreaInputInitialised === "true") {
    return;
  }

  areaInput.dataset.searchAreaInputInitialised = "true";

  try {
    await ensureGooglePlacesLoaded();
  } catch (error) {
    console.error(error);
    return;
  }

  const autocompleteService = new google.maps.places.AutocompleteService();
  let suggestions: google.maps.places.AutocompletePrediction[] = [];
  let activeSuggestionIndex = -1;

  const clearSuggestionsAndState = () => {
    suggestions = [];
    activeSuggestionIndex = -1;
    clearSuggestions(suggestionsContainer);
  };

  const selectSuggestion = (
    suggestion: google.maps.places.AutocompletePrediction
  ) => {
    areaInput.value = formatLocationLabel(suggestion.description);
    clearSuggestionsAndState();
  };

  const fetchAndRenderPredictions = debounce(async (searchValue: string) => {
    if (!searchValue.trim()) {
      clearSuggestionsAndState();
      return;
    }

    suggestions = await fetchPredictions(autocompleteService, searchValue);

    activeSuggestionIndex = -1;

    renderSuggestions(suggestionsContainer, suggestions);

    updateHighlightedSuggestion(suggestionsContainer, activeSuggestionIndex);
  }, GOOGLE_PREDICTION_DEBOUNCE_MS);

  areaInput.addEventListener("input", () => {
    void fetchAndRenderPredictions(areaInput.value);
  });

  areaInput.addEventListener("keydown", (keyboardEvent) => {
    if (suggestions.length === 0) {
      return;
    }

    if (keyboardEvent.key === "ArrowDown") {
      keyboardEvent.preventDefault();

      activeSuggestionIndex = Math.min(
        activeSuggestionIndex + 1,
        suggestions.length - 1
      );

      updateHighlightedSuggestion(suggestionsContainer, activeSuggestionIndex);

      return;
    }

    if (keyboardEvent.key === "ArrowUp") {
      keyboardEvent.preventDefault();

      activeSuggestionIndex = Math.max(activeSuggestionIndex - 1, 0);

      updateHighlightedSuggestion(suggestionsContainer, activeSuggestionIndex);

      return;
    }

    if (keyboardEvent.key === "Enter" && activeSuggestionIndex >= 0) {
      keyboardEvent.preventDefault();

      selectSuggestion(suggestions[activeSuggestionIndex]);

      return;
    }

    if (keyboardEvent.key === "Escape") {
      clearSuggestionsAndState();
    }
  });

  areaInput.addEventListener("blur", () => {
    window.setTimeout(clearSuggestionsAndState, 150);
  });

  areaInput.addEventListener("focus", () => {
    if (suggestions.length > 0) {
      suggestionsContainer.classList.remove("hidden");
    }
  });

  suggestionsContainer.addEventListener("click", (mouseEvent) => {
    const suggestionIndex = getClickedSuggestionIndex(mouseEvent.target);

    if (suggestionIndex === null || !suggestions[suggestionIndex]) {
      return;
    }

    selectSuggestion(suggestions[suggestionIndex]);
  });
};

const initSearchAreaInputs = () => {
  const areaInputs = document.querySelectorAll<HTMLInputElement>(
    SEARCH_AREA_INPUT_SELECTOR
  );

  areaInputs.forEach((areaInput) => {
    void initSearchAreaInput(areaInput);
  });
};

document.addEventListener("DOMContentLoaded", initSearchAreaInputs);
document.addEventListener("turbo:load", initSearchAreaInputs);
window.addEventListener("pageshow", initSearchAreaInputs);

export {};
